import { Sticky, useScreenSize } from "@highway/hwy-design-system";
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useArticle } from "../../../../../hooks/useArticle/useArticle";
import { LOCard } from "../LOCard";

const TableOfContents = () => {
    const { isXS } = useScreenSize();

    return isXS ? <MobileTOC /> : <DesktopTOC />;
};

const DesktopTOC = () => (
    <Stack
        sx={{
            backgroundColor: (theme) => theme.palette.primaryWhite.main,
            boxSizing: "border-box",
            overflow: "auto",
            maxHeight: "min(100vh, 1041px)",
            maxWidth: "360px",
            padding: "48px 32px",
            position: "fixed",
            top: 0,
            right: 0,
        }}
    >
        <ContentTable />
    </Stack>
);

const MobileTOC = () => (
    <Sticky order={0} position="bottom">
        <Accordion variant="card" sx={{ backgroundColor: (theme) => theme.palette.primaryWhite.main }}>
            <AccordionSummary>
                <Typography variant="body1Bold">Table of Contents</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <ContentTable />
            </AccordionDetails>
        </Accordion>
    </Sticky>
);

const ContentTable = () => {
    const { viewedArticle } = useArticle({ baseRoute: "newsletter" });
    const { isXS } = useScreenSize();

    const contents = useMemo(() => {
        if (viewedArticle?.body) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(viewedArticle.body, "text/html");
            const elements = doc.querySelectorAll("h3");

            if (elements.length > 0) {
                return [...elements].filter((el) => el.textContent).map((element) => element.textContent);
            }
        }
    }, [viewedArticle]);

    const scrollTo = (index: number) => {
        const target = document.querySelectorAll("h3")[index];

        if (target) {
            target.scrollIntoView({ behavior: "smooth" });
        }
    };

    if (!contents) return <></>;
    return (
        <Stack spacing={4}>
            <Stack spacing={1}>
                {!isXS && <Typography variant="overline">Table of Contents</Typography>}
                <ul
                    style={{
                        paddingLeft: "20px",
                        margin: 0,
                    }}
                >
                    {contents.map((element, index) => (
                        <li key={index}>
                            <span onClick={() => scrollTo(index)} style={{ cursor: "pointer" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textDecoration: "underline",
                                        color: (theme) => theme.palette.primaryPurple.main,
                                        display: "inline",
                                    }}
                                >
                                    {element}
                                </Typography>
                            </span>
                        </li>
                    ))}
                </ul>
            </Stack>
            {!isXS && <LOCard />}
        </Stack>
    );
};

export default TableOfContents;
